
import { ComplexValue } from 'app/types-shared';
import { Filter, AdvancedSearchField, FilterTypes, FilterOption, FilterMenuOptions } from 'app/components/filters/types';
import { EditingValues } from 'app/components/filters/filters-panel/types';
import { postfixOperator, storePostfix } from 'app/components/filters/filters-panel/constants';
import { SelectItem } from 'ui/common/select/types';
import { createFormatters } from 'app/infrastructure/data/date';
import { AdvancedSearchOption, FilterList, FilterReplacementsOption } from 'app/area/filters/types';
import { AdvancedSearchType } from 'app/area/favourite/types';
import { OptionItemSelections } from 'app/components/display-selected-options/display-selected-options';

export function mapToAdvancedSearchOptions(options: AdvancedSearchField[]): AdvancedSearchOption[] {
  return options.map(item => ({
    value: item.domain,
    description: item.label,
    descendants: !!item.children ? mapToAdvancedSearchOptions(item.children) : undefined,
    advancedSearchType: item.advancedSearchType,
    searchRequired: item.searchRequired
  } as AdvancedSearchOption));
}

export function mapToMultipleDomainNodeOptions(options: AdvancedSearchField[]): FilterReplacementsOption[] {
  return options.map(item => ({
    value: item.domainNode,
    optionDomainId: item.domain,
    description: item.label,
    descendants: !!item.children ? mapToMultipleDomainNodeOptions(item.children) : undefined,
    advancedSearchType: item.advancedSearchType,
    searchRequired: item.searchRequired
  } as FilterReplacementsOption));
}

export function mapToSingleDomainNodeOptions(options: AdvancedSearchField[]): FilterReplacementsOption[] {
  return options.map(item => ({
    value: item.domainNode,
    description: item.label,
    descendants: !!item.children ? mapToSingleDomainNodeOptions(item.children) : undefined,
    advancedSearchType: item.advancedSearchType,
    searchRequired: item.searchRequired
  } as FilterReplacementsOption));
}

export function mapToSelectedOptions(value: number | string | Date | SelectItem[], type: FilterTypes, locale: any) {

  const formatters = createFormatters(locale);
  const values = {
    [FilterTypes.SelectMultiple]: value,
    [FilterTypes.SelectMultipleFlat]: value,
    [FilterTypes.SelectMultipleHierarchy]: value,
    [FilterTypes.SelectMultipleFlatWithCategories]: value,
    [FilterTypes.SelectMultipleFlatSequentialWithCategories]: value,
    [FilterTypes.SelectMultipleHierarchyWithCategories]: value,
    [FilterTypes.SelectMultipleHierarchySequentialWithCategories]: value,
    [FilterTypes.TextField]: [{ description: String(value) }],
    [FilterTypes.DateField]: value && !!Object.keys(value).length ? [value] :
      (value instanceof Date && [{
        description: formatters.formatDate(value,
          { day: 'numeric', month: 'short', year: 'numeric' })
      }]),
    [FilterTypes.NumberField]: [{ description: String(value) }],
    [FilterTypes.CheckBox]: [{ description:  Array.isArray(value) ? value.map(v=>v.value) : value }],
    [FilterTypes.DecimalNumber]: [{ description: String(value) }],
    [FilterTypes.MyUnit]: value,
    [FilterTypes.MyLocation]: value,
    [FilterTypes.Person]: value,
    [FilterTypes.PredefFilters]: value
  };

  return (!!value || value === 0) && values[type];
}

export const mapToFilterList = (filters: Filter[], data: EditingValues, translation: any, locale: any): FilterList[] => {

  return data
    ? filters.map(filter =>
      [
        filter.id,
        ...Object.keys(storePostfix).map(key => filter.id + storePostfix[key])
      ]
        .filter(valueKey => data[valueKey] && (Array.isArray(data[valueKey].value)
          ? data[valueKey].value.length
          : data[valueKey].value || (data[valueKey].value as any) === 0))
        .map(valueKey => ({
          filter,
          selectedOptions: filter ? mapToSelectedOptions(data[valueKey].value, filter.advancedSearchType, locale) : undefined,
          key: valueKey.indexOf(postfixOperator.include) > 0 ? '' :
            (valueKey.indexOf(postfixOperator.exclude) > 0 ? translation.filters_excluded : null)
        }))
    )
      .reduce((memo, valueKeys) => [...memo, ...valueKeys], [])
    : [];
};

export const createDescription = (value: ComplexValue<any>, filterList: FilterList, translation?: any) => {
  const descriptions = {
    [FilterTypes.SelectMultiple]: value.description,
    [FilterTypes.SelectMultipleFlat]: value.description,
    [FilterTypes.SelectMultipleHierarchy]: value.description,
    [FilterTypes.SelectMultipleFlatWithCategories]: value.description,
    [FilterTypes.SelectMultipleFlatSequentialWithCategories]: value.description,
    [FilterTypes.SelectMultipleHierarchyWithCategories]: value.description,
    [FilterTypes.SelectMultipleHierarchySequentialWithCategories]: value.description,
    [FilterTypes.TextField]: value.description,
    [FilterTypes.DateField]: value.description,
    [FilterTypes.NumberField]: value.description,
    [FilterTypes.CheckBox]: String(value.description) === '1' ?
      translation && translation.filters_checkbox_checked : translation && translation.filters_checkbox_unchecked,
    [FilterTypes.DecimalNumber]: value.description,
    [FilterTypes.MyUnit]: value.allLevelDescription || value.description,
    [FilterTypes.MyLocation]: value.allLevelDescription || value.description,
    [FilterTypes.Person]: value.description,
    [FilterTypes.PredefFilters]: `${value.description} ${filterList.key && `[${filterList.key}]`}`
  };

  return descriptions[filterList.filter.advancedSearchType];
};

export const createTitle = (title: string, filterList: FilterList) => {
  const titles = {
    [FilterTypes.MyUnit]: title,
    [FilterTypes.MyLocation]: title,
    [FilterTypes.Person]: title
  };

  return titles[filterList.filter.advancedSearchType];
};

export const createExcludedValues = (menuOptions: FilterOption, translation: any) => {
  const options = {
    [FilterMenuOptions.Exclude]: translation.filter_summary_exclude,
    [FilterMenuOptions.ExcludeSubLevels]: translation.filter_summary_exclude_sub
  };

  return options[menuOptions.option];
};

export const selectedOptionsByFilterItem = (filterList: FilterList[], translation: any, locale: any): OptionItemSelections => {
  
  const formatters = createFormatters(locale);

  const values = {
    advancedSearchType: filterList[0].filter.advancedSearchType,
    descriptions: filterList.map(filterListItem => filterListItem.selectedOptions
      .map(option => createDescription(option, filterListItem, translation))),
    titles: filterList.map(filterListItem => filterListItem.selectedOptions
      .map(option => option.title && createTitle(option.title, filterListItem))),
    excludedValues: filterList[0].filter.menuOptions && filterList[0].filter.menuOptions
      .map(option => option && createExcludedValues(option, translation))
      .filter(option => !!option)
  };

  const rangeFormatter = () => {
    const firstDescription = values.descriptions[0];
    const secondDescription = values.descriptions[1];
    const isDecimalNumber = values.advancedSearchType === FilterTypes.DecimalNumber;
    const formatNumber = (valueDescription) => formatters.formatNumber(Number(valueDescription), {}).toString();

    const valueFirst = isDecimalNumber && firstDescription !== undefined ? formatNumber(firstDescription) : firstDescription;
    const valueSecond = isDecimalNumber && secondDescription !== undefined ? formatNumber(secondDescription) : secondDescription;

    return [(valueFirst && valueSecond) ? (valueFirst + ' - ' + valueSecond) : ((valueFirst && !valueSecond) ? valueFirst : valueSecond)];
  };

  const predefinedFiltersFormatter = () => {
    return values.descriptions.reduce((previousValue, currentValue) => [...previousValue, ...currentValue]);
  };

  const defaultFiltersFormatter = () => {
    return values.descriptions.reduce((previousValue, currentValue) => [...currentValue, ...previousValue]);
  }; 

  return {
    values: [
      FilterTypes.DateField,
      FilterTypes.DecimalNumber
    ].indexOf(values.advancedSearchType) > -1 ? rangeFormatter() :
      (values.advancedSearchType === FilterTypes.PredefFilters ? predefinedFiltersFormatter() : defaultFiltersFormatter()),
    titles: values.titles.reduce((previousValue, currentValue) => [...currentValue, ...previousValue]),
    excludedValues: values.excludedValues
  };
};

export const getSelectedOptionsIds = (editingValues: EditingValues = {}, filterDomainId: number) => {
  return Object.keys(editingValues)
    .filter(key => Number(key.split('_')?.[0]) === filterDomainId)
    .map(key => editingValues[key] && editingValues[key].value)
    .reduce((memo, editingValue) => {
      return editingValue ? [
        ...editingValue.map(v => v.value),
        ...memo
      ] : memo;
    }, []) as number[];
};

export const getCategoryOptionsParams = (type: FilterTypes) => {
  
  const displayNotSelectedFalse = { displayNotSelected: true };

  const categoryOptionParams = {
    [FilterTypes.SelectMultipleFlatWithCategories]: displayNotSelectedFalse,
    [FilterTypes.SelectMultipleFlatSequentialWithCategories]: displayNotSelectedFalse,
    [FilterTypes.SelectMultipleHierarchyWithCategories]: displayNotSelectedFalse,
    [FilterTypes.SelectMultipleHierarchySequentialWithCategories]: displayNotSelectedFalse
  };
  
  return type && categoryOptionParams[type];
};

export function mapToAdvancedSearchTypes(options: AdvancedSearchOption[]): AdvancedSearchType[] {
  return options.map(item => ({
    domain: item.value,
    label: item.description,
    advancedSearchType: item.advancedSearchType,
    searchRequired: item.searchRequired,
    added: true
  }));
}

export const createFilteredValues = (values: any, validation: any) => {
  return Object.keys(values).reduce((memo, key) => {
    return validation[key]
      ? memo
      : {
        ...memo,
        [key]: values[key]
      };
  }, {});
};
